/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000111 名称照会
 */

import { Column, Query } from "@material-table/core";
import {
  B000111Find,
  B000111FindRequest,
  B000111FindTenpudenpyoCd,
  B000111FindTenpudenpyoCdRequest,
  B000111Get,
  B000111GetRequest,
  catchRestError,
} from "clients/rest";
import {
  SText,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { meishoKbn } from "services/codes";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
export type B000111Props = Partial<{
  meishoKbn: string;
  denpyoshubetsuCd: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  meishoCdFrom: string;
  meishoCdTo: string;
  name: string;
  denpyoshubetsuCd: string;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: number;
  meishoCd: string;
  meishoNm1: string;
  meishoKana1: string;
  meishoKbn: string;
  meishoNm2: string;
  meishoKana2: string;
  sujikomoku1: string;
  sujikomoku2: string;
  kbn1: string;
  sakujoyoteikbn: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  meishoCdFrom: "",
  meishoCdTo: "",
  name: "",
  denpyoshubetsuCd: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "4rem", align: "right" },
  { field: "meishoCd", title: "コード", width: "6rem" },
  { field: "meishoNm1", title: "名称", width: "20rem" },
  { field: "meishoKana1", title: "名称カナ", width: "20rem" },
  { field: "meishoKbn", hidden: true },
  { field: "meishoNm2", hidden: true },
  { field: "meishoKana2", hidden: true },
  { field: "sujikomoku1", hidden: true },
  { field: "sujikomoku2", hidden: true },
  { field: "kbn1", hidden: true },
  { field: "sakujoyoteikbn", hidden: true },
];

const focusId = "B000111";

export const B000111 = (props: B000111Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: B000111Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログの開閉を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // タイトル設定
  const [title, setTitle] = useState("名称照会");

  // 初期表示イベント
  useEffect(() => {
    (async () => {
      // 初回フォーカス先
      focus(1);
      // 処理詳細１
      // タイトル処理
      // b000111/get を呼び出して結果を取得する。
      // 検索パラメータを作成する。
      if (typeof props.meishoKbn === "undefined") {
        handleSubmit();
        return;
      }
      const param: B000111GetRequest = {
        meishoKbn: "9000",
        meishoCd: props.meishoKbn,
      };
      // API呼び出し
      await B000111Get(param).then((value) => {
        // 取得した名称 1 を「名称 1+照会」で設定する。
        setTitle(`${value.data.meishoNm1}照会`);
      });
    })();
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      if (typeof props.meishoKbn === "undefined") {
        return;
      }

      // 添付伝票照会の場合
      if (props.meishoKbn === meishoKbn.tenpudenpyo) {
        // 検索パラメータを作成する。
        if (typeof props.denpyoshubetsuCd === "undefined") {
          return;
        }

        const param: B000111FindTenpudenpyoCdRequest = {
          denpyoshubetsuCd: props.denpyoshubetsuCd,
          meishoKbn: props.meishoKbn,
          meishoCdFrom: fields.meishoCdFrom,
          meishoCdTo: fields.meishoCdTo,
          name: fields.name,
          // ページネーションの設定
          page: query.page,
          size: query.pageSize,
          // ソートの設定
          // ["出力項目名,順番(asc/desc", ...]
          sort: ["meishoCd,asc"],
        };
        // API呼び出し
        B000111FindTenpudenpyoCd(param)
          // 正常取得時
          .then((value) => {
            const offset = query.pageSize * query.page;
            resolve({
              data: mapIds(value.data.content, "gyoNo", offset),
              page: value.data.number,
              totalCount: value.data.totalElements,
            });
          })
          // エラー時
          .catch((reason) => {
            catchRestError(reason, navigate, setMsg);
            resolve({ data: [], page: 0, totalCount: 0 });
          })
          .finally(() => {
            // 初期ページ表示をオフにする。
            isDefaultPage.current = false;
          });
      }
      // 添付伝票照会の場合
      else {
        const param: B000111FindRequest = {
          meishoKbn: props.meishoKbn,
          meishoCdFrom: fields.meishoCdFrom,
          meishoCdTo: fields.meishoCdTo,
          name: fields.name,
          // ページネーションの設定
          page: query.page,
          size: query.pageSize,
          // ソートの設定
          // ["出力項目名,順番(asc/desc", ...]
          sort: ["meishoCd,asc"],
        };
        // API呼び出し
        B000111Find(param)
          // 正常取得時
          .then((value) => {
            const offset = query.pageSize * query.page;
            resolve({
              data: mapIds(value.data.content, "gyoNo", offset),
              page: value.data.number,
              totalCount: value.data.totalElements,
            });
          })
          // エラー時
          .catch((reason) => {
            catchRestError(reason, navigate, setMsg);
            resolve({ data: [], page: 0, totalCount: 0 });
          })
          .finally(() => {
            // 初期ページ表示をオフにする。
            isDefaultPage.current = false;
          });
      }
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame title={title} gridTemplateColumns="6rem 6rem 1rem 6rem 2rem">
        <SLabel>コード</SLabel>
        <SNumberField name="meishoCdFrom" code size={10} {...register(1)} />
        <SText>～</SText>
        <SNumberField name="meishoCdTo" code size={10} {...register(2)} />
        <SLabel gridColumn="1">名称</SLabel>
        <STextField
          name="name"
          size={40}
          gridColumn="span 3"
          {...register(3)}
        />
        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          gridRow="3"
          gridColumn="5"
          {...register(4)}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
